/**
 * Conponent for a textarea
 */

import {Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, SimpleChange, ViewChild, HostListener} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'messagefield',
    templateUrl: './input.messagefield.component.html'
})
export class MessagefieldComponent implements OnInit, OnChanges {
    /**
     * Text for the label
     */
    @Input() label: string;

    /**
     * Text in case of errors
     */
    @Input() error: string;

    /**
     * name for the field
     */
    @Input() name: string;

    /**
     * initial value for the field
     */
    @Input() value: string;

    /**
     * is this a required field or not
     */
    @Input() required: boolean;

    /**
     * the modelobject for the value
     */
    @Input() model: any;

    /**
     * extra class for the field wrapper
     */
    @Input() wrapperClass: string;

    /**
     * form object for the validation
     */
    @Input() form: FormGroup;

    /**
     * Number of rows for the input
     */
    @Input() rows: number = 1;

    /**
     * Label should be always visible or not
     */
    @Input() showLabel: boolean = false;

    /**
     * maximal number of the input in this field.
     */
    @Input() maxLength: number = 500;

    /**
     * Toggle for switching dyamic height on and of..
     */
    @Input() dynamicHeight: boolean = false;

    /**
     * textkey in case we want to show a infotext
     * @type {null}
     */
    @Input() infotTextkey: string = null;

    /**
     * Triggerwords that should activate the infotext.
     */
    @Input() infotextTriggerWords: string[] = [];

    /**
     * toggle for autocomplete attribute
     */
    @Input() autocomplete: boolean = true;

    /**
     * eventemitter if the value changes
     * @type {EventEmitter<any>}
     */
    @Output() modelChange: EventEmitter<any> = new EventEmitter();

    /**
     * fires if the textfields is blurred
     */
    @Output() onBlur: EventEmitter<void> = new EventEmitter<void>();

    /**
     * Does the field has a value or not
     * @type {boolean}
     */
    hasValue: boolean = false;

    /**
     * can be set manually to set a special placeholder that is different from the label
     *
     * @type {String}
     */
    placeholder: string = null;

    /**
     * Controls if the infotext should be whon or not
     * @type {boolean}
     */
    showInfotext: boolean = false;

    /**
     * Holds the input element.
     */
    @ViewChild('messagefield') input;

    /**
     * constructor
     * @param translate
     */
    public constructor(private translate: TranslateService) {}

    /**
     * called if the component is initialized
     */
    public ngOnInit(): void {
        if(this.infotTextkey !== null) {
            this.wrapperClass += ' info';
        }

        if(this.value && this.value.length > 0) {
            this.hasValue = true;
        }
    }

    /**
     * is triggered every time the model changes (from outside) and updates the css classes
     *
     * @param {SimpleChanges} changes
     */
    ngOnChanges(changes: SimpleChanges) {
        if(changes.model && changes.model.currentValue) {
            this.hasValue = (changes.model.currentValue.length > 0);

            // if there is a value and an infotext and triggerwords set, show the infotext if a triggerword is found
            if(this.hasValue === true && this.infotTextkey != null && this.infotextTriggerWords.length > 0) {
                if(this.infotextTriggerWords.findIndex(triggerWord => changes.model.currentValue.toLowerCase().indexOf(triggerWord) > -1) > -1) {
                    // show the infotext because we found at least one triggerword
                    this.showInfotext = true;
                }
            }
        }
    }

    /**
     * Returns if the field is valid or not
     * @returns {boolean}
     */
    elementValid(): boolean {
        if(!this.form.controls[this.name]) {
            return false;
        }

        return this.form.controls[this.name].disabled ||
            this.form.controls[this.name].touched == false ||
            this.form.controls[this.name].valid == true;
    }

    /**
     * Function to update the model and inform the parent Component about it.
     * We can not use databinding here because it will not work together with
     * @param event
     */
    updateModel(event:any) {
        this.model = event.target.value;
        this.modelChange.emit(this.model);
        this.hasValue = (this.model.length > 0);
    }

    /**
     * Update the height of the field
     * @param event
     */
    @HostListener('document:keyup', ['$event'])
    updateHeightDynamic(event: KeyboardEvent): void {
        if(this.dynamicHeight === true && this.input.nativeElement) {
            this.input.nativeElement.style.height = "";
            this.input.nativeElement.style.height = this.input.nativeElement.scrollHeight + "px";
        }
    }

    /**
     * Is called if the user clicks the body
     * @param event
     */
    @HostListener('window:click', ['$event'])
    onClickOutside(event: any): void {
        if(this.showInfotext == true && event.target.localName != "i") {
            this.showInfotext = false;
        }
    }

    /**
     * Toggle the infotext
     * @param event
     */
    toggleInfotext(event: Event): void {
      event.preventDefault();
      this.showInfotext = !this.showInfotext;
    }
}
